import { useMemo } from 'react'

import { useCurrentUser } from 'common/hooks'
import { NodeType } from 'Automations/nodeDefinitions/types'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

export type AddNodeItem = {
  nodeType: NodeType
  config?: Record<string, unknown>
  /**
   * Whether this node can be inserted inline on an edge. Terminal/standalone
   * nodes (End, Note) have no usable output handle to splice a downstream
   * connection onto, so they are offered in the toolbar but not the edge picker.
   */
  insertable: boolean
  /**
   * Whether this node has an input handle, i.e. it can be the target of a
   * connection. Used by the connection-drop picker (Note has no handles).
   */
  acceptsInput: boolean
}

/**
 * Single source of truth for the nodes a user can add to the canvas. Consumed
 * by the bottom AddNodeToolbar, the inline edge insert menu and the
 * connection-drop picker so they stay in sync.
 */
export const useAddNodeItems = (): AddNodeItem[] => {
  const flags = useFeatureFlags()
  const { email: currentUserEmail } = useCurrentUser()

  return useMemo(
    () => [
      ...(flags.useAutomationsDunning
        ? [
            {
              nodeType: 'REMINDER' as NodeType,
              insertable: true,
              acceptsInput: true
            }
          ]
        : []),
      { nodeType: 'SWITCH', insertable: true, acceptsInput: true },
      { nodeType: 'REVIEW_PLACEHOLDER', insertable: true, acceptsInput: true },
      { nodeType: 'ACTION_PLACEHOLDER', insertable: true, acceptsInput: true },
      { nodeType: 'END', insertable: false, acceptsInput: true },
      {
        nodeType: 'NOTE',
        config: { createdByEmail: currentUserEmail },
        insertable: false,
        acceptsInput: false
      }
    ],
    [flags.useAutomationsDunning, currentUserEmail]
  )
}

export const useInsertableNodeItems = (): AddNodeItem[] => {
  const items = useAddNodeItems()
  return useMemo(() => items.filter(item => item.insertable), [items])
}

/** Nodes offered when a connection is dropped on empty canvas — anything that
 * can receive the dragged connection (i.e. has an input handle). */
export const useConnectionDropNodeItems = (): AddNodeItem[] => {
  const items = useAddNodeItems()
  return useMemo(() => items.filter(item => item.acceptsInput), [items])
}
